"use client"

import useConditionalRenderingAnimation from "@/hooks/useConditionalRenderingAnimation"
import LanguageIcon from "@/layout/icons/LanguageIcon"
import { useConfigStore } from "@/store/config-store"
import { cn } from "@/utils/utils"
import { Label } from "@ui-library/typography/Labels"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"

const LanguageSelector = () => {
  const strings = useConfigStore.getState().strings
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { shouldRender, isAnimating } = useConditionalRenderingAnimation(isOpen, 300)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const items = [
    { url: "/", label: strings?.header.language_selector_sv },
    { url: "/en", label: strings?.header.language_selector_en }
  ]

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false)
        buttonRef.current?.focus()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown)
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative block max-xl:hidden" ref={menuRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        id="language-selector-button"
        className="h-[72px] w-14"
        aria-expanded={isOpen}
        aria-controls="language-selector-menu"
        aria-haspopup="true"
        aria-label={strings?.header.language_selector}
      >
        <LanguageIcon />
      </button>

      {shouldRender && (
        <div
          id="language-selector-menu"
          aria-labelledby="language-selector-button"
          role="menu"
          className={cn(
            "absolute left-2/4 top-full -translate-x-2/4 pt-4 transition-[opacity,_transform] duration-300",
            !isAnimating && "translate-y-6 opacity-0",
            isAnimating && "translate-y-0 opacity-100"
          )}
        >
          <div className={cn("relative w-full rounded-lg bg-surface drop-shadow-lg")}>
            <span className="absolute -top-1 left-2/4 -ml-2 block h-4 w-4 rotate-45 bg-surface" />

            <ul className="min-w-[160px] p-4">
              {items.map((item, i) => (
                <li key={i} className="m-2" role="none">
                  <Label size="small" asChild>
                    <Link
                      role="menuitem"
                      href={item.url}
                      target="_top"
                      className="block cursor-pointer text-on-background transition-colors hover:text-primary-50"
                    >
                      {item.label}
                    </Link>
                  </Label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
